import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';

import { State } from 'redux/initialState';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import menuTypes from 'constants/menuTypes';
import { store } from 'redux/store';

import { TradingOffer, TradingOfferType } from 'types/TradingOffer';
import { TradingOfferOperation } from 'types/TradingOfferOperation';
import { Partner } from 'types';
import { SocietyEquityMarketStatus } from 'types/Society';

import { updateSocietyConfiguration } from 'modules/society/redux/societyActions';
import { togglePartnerEquityMarketAccess } from 'modules/partners/redux/partnerActions';
import { useGetTradingOfferOptions } from 'modules/trading/hooks/useGetTradingOfferOptions';
import { useGetTradingOffers } from 'modules/trading/hooks/useGetTradingOffers';
import { EquityMarketAdminPanelContextType } from '../types/EquityMarketAdminPanelContextType';
import { equityMarketAdminPanelInitValue } from '../constants/EquityMarketAdminPanelInitValue';
import { getTradingOfferOperations } from '../../../redux/tradingOfferOperationActions';
import { tabs } from '../constants/Tabs';

export const EquityMarketAdminPanelContext =
  createContext<EquityMarketAdminPanelContextType>(
    equityMarketAdminPanelInitValue
  );

export const EquityMarketAdminPanelProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId } = useParams<{ societyId: string }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const partnerId = queryParams.get('partnerId');
  const partnerQuery = partnerId ? `?partnerId=${partnerId}` : '';

  const actualSociety = useSelector(
    ({ society }: State) => society?.actualSociety
  );
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );
  const tradingOffers = useSelector(
    ({ tradingOffers }: State) => (tradingOffers ?? []) as TradingOffer[]
  );
  const partners = useSelector(
    ({ partners }: State) => (partners ?? []) as Partner[]
  );

  const [offersPage, setOffersPage] = useState<number>(1);
  const [offersRowsPerPage, setOffersRowsPerPage] = useState<number>(10);
  const [selectedTab, setSelectedTab] = useState<string>(tabs.SELL);
  const [operations, setOperations] = useState<TradingOfferOperation[]>([]);
  const [isOperationsLoading, setIsOperationsLoading] = useState(true);

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const { data: tradingOffersData, isLoading: isTradingOffersLoading } =
    useGetTradingOffers({
      societyId,
      page: offersPage,
      rowsPerPage: offersRowsPerPage,
      tradingOfferType:
        selectedTab === tabs.SELL
          ? TradingOfferType.SELL
          : TradingOfferType.BUY,
    });

  const {
    data: tradingOfferOptionsData,
    actions: tradingOfferOptionsActions,
    isLoading: isTradingOfferOptionsLoading,
  } = useGetTradingOfferOptions({
    societyId,
  });

  useEffect(() => {
    if (!actualSociety || !societyId) return;

    const controller = new AbortController();

    const fetchOperations = async () => {
      try {
        setIsOperationsLoading(true);
        const result = await store.dispatch(
          getTradingOfferOperations({ societyId })
        );
        if (!controller.signal.aborted && result) {
          setOperations(result.data || []);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsOperationsLoading(false);
        }
      }
    };

    fetchOperations();

    return () => controller.abort();
  }, [actualSociety, societyId]);

  const equityMarketStatus: SocietyEquityMarketStatus =
    actualSociety?.configuration?.equityMarket?.status ||
    SocietyEquityMarketStatus.OPEN;

  const [isStatusChanging, setIsStatusChanging] = useState(false);

  const handleEquityMarketStatusChange = useCallback(
    async (status: SocietyEquityMarketStatus) => {
      if (!societyId) return;
      try {
        setIsStatusChanging(true);
        await dispatch(
          updateSocietyConfiguration(
            societyId,
            { 'equityMarket.status': status },
            true
          )
        );
      } finally {
        setIsStatusChanging(false);
      }
    },
    [dispatch, societyId]
  );

  const handleGoBack = useCallback(() => {
    history.push(`/mercado-de-acciones/${societyId}${partnerQuery}`);
  }, [history, societyId, partnerQuery]);

  const handleViewOfferDetail = useCallback(
    (offerId: string) => {
      history.push(`/detalle-oferta/${societyId}/${offerId}${partnerQuery}`);
    },
    [history, societyId, partnerQuery]
  );

  const handleViewOperationDetail = useCallback(
    (operationId: string) => {
      history.push(
        `/detalle-operacion-oferta/${societyId}/${operationId}${partnerQuery}`
      );
    },
    [history, societyId, partnerQuery]
  );

  const handleTogglePartnerEquityMarketAccess = useCallback(
    async (partnerIds: string[], enabled: boolean) => {
      await dispatch(togglePartnerEquityMarketAccess({ partnerIds, enabled }));
    },
    [dispatch]
  );

  const handleChangeOffersPage = (page: number) => {
    setOffersPage(page);
  };

  const handleChangeOffersRowsPerPage = (rowsPerPage: number) => {
    setOffersRowsPerPage(rowsPerPage);
    setOffersPage(1);
  };

  const handleChangeTab = (newTab: string) => {
    setSelectedTab(newTab);
  };

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        equityMarketStatus,
        tradingOffers,
        sellOffersCount: actualSociety?.tradingOffersCount?.sell || 0,
        buyOffersCount: actualSociety?.tradingOffersCount?.buy || 0,
        statusCounts: tradingOffersData?.statusCounts,
        selectedTab,
        selectedTradingOfferOption:
          tradingOfferOptionsData?.selectedTradingOfferOption || null,
        tradingOfferOptions: tradingOfferOptionsData?.tradingOfferOptions || [],
        operations,
        tradingOfferOperationsCount: actualSociety?.tradingOfferOperationsCount,
        partners,
        isAdmin,
        isLoading:
          isTradingOffersLoading ||
          isOperationsLoading ||
          isStatusChanging ||
          !actualSociety,
      },
      actions: {
        handleGoBack,
        handleChangeOffersPage,
        handleChangeOffersRowsPerPage,
        handleChangeTab,
        handleChangeTradingOfferOption:
          tradingOfferOptionsActions.handleChangeTradingOfferOption,
        handleSearchTradingOffer:
          tradingOfferOptionsActions.handleSearchTradingOffer,
        handleEquityMarketStatusChange,
        handleViewOfferDetail,
        handleViewOperationDetail,
        handleTogglePartnerEquityMarketAccess,
      },
    }),
    [
      societyId,
      actualSociety,
      equityMarketStatus,
      tradingOffers,
      tradingOffersData?.statusCounts,
      selectedTab,
      tradingOfferOptionsData?.selectedTradingOfferOption,
      tradingOfferOptionsData?.tradingOfferOptions,
      operations,
      partners,
      isAdmin,
      isTradingOffersLoading,
      isOperationsLoading,
      isStatusChanging,
      handleGoBack,
      tradingOfferOptionsActions.handleChangeTradingOfferOption,
      tradingOfferOptionsActions.handleSearchTradingOffer,
      handleEquityMarketStatusChange,
      handleViewOfferDetail,
      handleViewOperationDetail,
      handleTogglePartnerEquityMarketAccess,
    ]
  );

  return (
    <EquityMarketAdminPanelContext.Provider value={contextValue}>
      {children}
    </EquityMarketAdminPanelContext.Provider>
  );
};

export const useEquityMarketAdminPanel = () => {
  const context = useContext(EquityMarketAdminPanelContext);
  if (!context) {
    throw new Error(
      'useEquityMarketAdminPanel must be used within an EquityMarketAdminPanelProvider'
    );
  }
  return context;
};
